/* ===== Notifications Page ===== */

import { useEffect, useState } from 'react'
import type { Notification } from '@zetyar/domain'
import { Bell } from 'lucide-react'
import { useApp } from '@/presentation/providers/useApp'
import { Card, CardContent, CardHeader, CardTitle } from '@/presentation/components/ui/Card'
import { createWebTenantContext } from '@/infrastructure/repositories/announcements'
import { webNotificationRepository } from '@/infrastructure/repositories/notifications'

function formatDate(value: string): string {
  return new Date(value).toLocaleString('es-CL', { dateStyle: 'medium', timeStyle: 'short' })
}

export function NotificationsPage() {
  const { currentMembership, currentOrganization, profile, user } = useApp()
  const [notifications, setNotifications] = useState<Notification[]>([])
  const [isLoading, setIsLoading] = useState(true)

  const tenantContext = () => {
    if (!currentOrganization || !profile || !user) return null
    return createWebTenantContext({
      authUid: user.uid,
      personId: profile.id,
      organizationId: currentOrganization.id,
      membershipId: currentMembership?.id,
    })
  }

  const load = async () => {
    const context = tenantContext()
    if (!context) {
      setNotifications([])
      setIsLoading(false)
      return
    }

    try {
      setIsLoading(true)
      const result = await webNotificationRepository.list(context, 100)
      setNotifications(result)
    } catch (error) {
      console.error('Error al cargar notificaciones:', error)
      setNotifications([])
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    let isMounted = true
    const run = async () => {
      if (!isMounted) return
      await load()
    }
    void run()
    return () => {
      isMounted = false
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentMembership, currentOrganization, profile, user])

  const handleOpen = async (notification: Notification) => {
    if (notification.isRead) return

    const context = tenantContext()
    if (!context) return

    try {
      await webNotificationRepository.markRead(context, notification.id)
      setNotifications((current) =>
        current.map((item) => (item.id === notification.id ? { ...item, isRead: true } : item)),
      )
      window.dispatchEvent(new CustomEvent('notifications-read'))
    } catch (error) {
      console.error('Error al marcar la notificacion como leida:', error)
    }
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-neutral-900 dark:text-neutral-100 tracking-tight">
          Notificaciones
        </h1>
        <p className="text-sm text-neutral-500 dark:text-neutral-400 mt-1">
          Avisos sobre tus solicitudes, gastos y membresia en {currentOrganization?.name}
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <Bell className="w-5 h-5 text-indigo-600" />
            Bandeja
          </CardTitle>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <p className="text-sm text-neutral-500 dark:text-neutral-400 animate-pulse">Cargando...</p>
          ) : notifications.length === 0 ? (
            <p className="text-sm text-neutral-500 dark:text-neutral-400">No tienes notificaciones.</p>
          ) : (
            <div className="space-y-2">
              {notifications.map((notification) => (
                <button
                  key={notification.id}
                  type="button"
                  onClick={() => void handleOpen(notification)}
                  className={`w-full text-left p-4 rounded-md border transition-colors ${
                    notification.isRead
                      ? 'border-neutral-200 dark:border-neutral-800'
                      : 'border-indigo-300 dark:border-indigo-700 bg-indigo-50/50 dark:bg-indigo-950/20'
                  }`}
                >
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <h3 className="font-semibold text-neutral-900 dark:text-neutral-100">{notification.title}</h3>
                      {notification.body && (
                        <p className="text-sm text-neutral-600 dark:text-neutral-300 mt-1">{notification.body}</p>
                      )}
                    </div>
                    {!notification.isRead && (
                      <span className="w-2 h-2 rounded-full bg-indigo-500 shrink-0 mt-1.5" aria-label="Sin leer" />
                    )}
                  </div>
                  <p className="text-xs text-neutral-400 mt-2">{formatDate(notification.createdAt)}</p>
                </button>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
